/**
 * Single source of truth for the UI language preference.
 *
 * Framework-free (no React, no i18next import) so it can be used by the
 * i18next bootstrap (`src/i18n.ts`), `LanguageContext`, and the axios clients
 * (`services/api.ts`, the demo client) without import cycles.
 *
 * Resolution order everywhere: `?lang=` query string → stored preference →
 * browser languages → English. The query string wins so a shared link such as
 * `/demo?lang=es` opens in that language even for a returning visitor.
 */

export const SUPPORTED_LANGUAGE_CODES = ['en', 'es', 'fr', 'ht', 'pt', 'zh', 'vi', 'ar', 'ko', 'ru'] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGE_CODES[number];

export const DEFAULT_LANGUAGE: SupportedLanguage = 'en';

/** The one localStorage key holding the user's language choice. */
export const LANGUAGE_STORAGE_KEY = 'preferred_language';

/**
 * Keys written by earlier versions (i18next's detector cache and an unused
 * `language` key). Read once for migration, never written.
 */
const LEGACY_STORAGE_KEYS = ['i18nextLng', 'language'] as const;

const RTL_LANGUAGES: ReadonlySet<string> = new Set(['ar']);

/** Normalise a tag such as `pt-BR` / `ZH_cn` to a supported code, or null. */
export const normalizeLanguage = (code: string | null | undefined): SupportedLanguage | null => {
  if (!code) return null;
  const base = code.trim().toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LANGUAGE_CODES as readonly string[]).includes(base) ? (base as SupportedLanguage) : null;
};

export const isSupportedLanguage = (code: string | null | undefined): code is SupportedLanguage =>
  !!code && normalizeLanguage(code) === code;

export const isRTLLanguage = (code: string | null | undefined): boolean => RTL_LANGUAGES.has(code || '');

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

/** Stored preference (migrating legacy keys), or null. */
export const getStoredLanguage = (): SupportedLanguage | null => {
  const current = normalizeLanguage(safeGet(LANGUAGE_STORAGE_KEY));
  if (current) return current;
  for (const key of LEGACY_STORAGE_KEYS) {
    const legacy = normalizeLanguage(safeGet(key));
    if (legacy) return legacy;
  }
  return null;
};

export const storeLanguage = (code: SupportedLanguage): void => {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
  } catch {
    // Private mode / storage disabled: the choice lasts for this page only.
  }
};

/** `?lang=` from the current URL, or null. */
export const getQueryLanguage = (search: string = typeof window !== 'undefined' ? window.location.search : ''): SupportedLanguage | null => {
  try {
    return normalizeLanguage(new URLSearchParams(search).get('lang'));
  } catch {
    return null;
  }
};

export const getBrowserLanguage = (): SupportedLanguage | null => {
  if (typeof navigator === 'undefined') return null;
  const candidates = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const tag of candidates) {
    const code = normalizeLanguage(tag);
    if (code) return code;
  }
  return null;
};

/** Initial language: query string → stored preference → browser → English. */
export const detectLanguage = (): SupportedLanguage =>
  getQueryLanguage() || getStoredLanguage() || getBrowserLanguage() || DEFAULT_LANGUAGE;

/**
 * Language to send to the backend (`Accept-Language`, `preferredLanguage`).
 * Reads the stored choice, which LanguageContext keeps in sync with the UI.
 */
export const getRequestLanguage = (): SupportedLanguage => getStoredLanguage() || detectLanguage();
